import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import './presentation_root.less';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import autobind from 'class-autobind';

import each from 'lodash/each';
import isEmpty from 'lodash/isEmpty';

import FullscreenManager from 'react-fullscreen-crossbrowser';

import PresentationProcessing from './presentation.processing.jsx';
import PresentationUi from './presentation_root.ui.jsx';

class Presentation extends Component {

  constructor() {
    super();

    this.state = {
      currentSlideIdx: 0,
      isFullscreenEnabled: false,
      hasInteractedWithPresentation: false
    };

    this.slickSlider = null;

    autobind(this);
  }

  preloadSlides() {
    each(this.props.slides, (imgSrc) => {
      const image = new Image();
      image.src = imgSrc;
    });
  }

  componentWillMount() {
    this.preloadSlides();
  }

  componentDidMount() {
    // This is a react-slick hack which allows us to focus
    // https://github.com/akiran/react-slick/issues/738
    if (this.slickSlider && this.props.focusOnMount) {
      this.slickSlider.innerSlider.list.setAttribute('tabindex', 0);
      this.slickSlider.innerSlider.list.focus();
    }

    if (this.props.onPresentationLoad) {
      this.props.onPresentationLoad();
    }
  }

  handleNextClick() {
    const { slides } = this.props;
    const { currentSlideIdx } = this.state;

    if (slides.length === currentSlideIdx + 1) {
      return;
    }

    if (this.slickSlider) {
      this.slickSlider.slickNext();
    }

    this.setState({
      currentSlideIdx: currentSlideIdx + 1
    });
  }

  handlePrevClick() {
    const { currentSlideIdx } = this.state;

    if (currentSlideIdx === 0) {
      return;
    }

    if (this.slickSlider) {
      this.slickSlider.slickPrev();
    }

    this.setState({
      currentSlideIdx: currentSlideIdx - 1
    });
  }

  handleGoToSlideClick(slideIdx) {
    const { slides } = this.props;

    if (slideIdx < 0 || slideIdx > slides.length) {
      return;
    }

    if (this.slickSlider) {
      this.slickSlider.slickGoTo(slideIdx);
    }

    this.setState({
      currentSlideIdx: slideIdx
    });
  }

  handleFullscreenClickAndFocus(isFullscreenEnabled) {
    this.setState({ isFullscreenEnabled });

    if (this.slickSlider) {
      this.slickSlider.innerSlider.list.setAttribute('tabindex', 0);
      this.slickSlider.innerSlider.list.focus();
    }
  }

  handleAfterSlideChange(currentIdx) {
    const { onSlideDisplay } = this.props;

    if (onSlideDisplay) {
      onSlideDisplay(/* slideNum = */ currentIdx + 1);
    }
  }

  handleDownloadClick() {
    const { downloadLink, canDownloadAsset, onDownloadClick } = this.props;

    if (!canDownloadAsset) {
      return;
    }

    if (onDownloadClick) {
      onDownloadClick();
    }

    window.open(downloadLink, '_blank');
  }

  handlePresentationClick() {
    const { onSlideDisplay } = this.props;
    const { hasInteractedWithPresentation } = this.state;

    // we want to know when to fire the initial slide display event and
    // to get an appropriate time we wait until they actually click in
    // the wrapper to interact with things.
    if (!hasInteractedWithPresentation && onSlideDisplay) {
      this.setState({ hasInteractedWithPresentation: true });
      onSlideDisplay(/* slideNum = */ 1);
    }
  }

  render() {
    const {
      currentSlideIdx,
      isFullscreenEnabled
    } = this.state;

    const {
      slides,
      height,
      downloadLink,
      canDownloadAsset,
      displayFilename,
      shouldIncludeSkipLink
    } = this.props;

    if (isEmpty(slides)) {
      return (
        <PresentationProcessing
          canDownloadAsset={canDownloadAsset}
          downloadLink={downloadLink}
          displayFilename={displayFilename}
          onDownloadClick={this.handleDownloadClick}
        />
      );
    }

    return (
      <FullscreenManager
        enabled={isFullscreenEnabled}
        onChange={(isFullscreenEnabled)=> {
          this.setState({ isFullscreenEnabled });
        }}
      >
        <PresentationUi 
          height={height}
          slides={slides}
          slickSliderRef={c => this.slickSlider = c}
          shouldIncludeSkipLink={!isFullscreenEnabled && shouldIncludeSkipLink}
          currentSlideIdx={currentSlideIdx}
          displayFilename={displayFilename}
          canDownloadAsset={canDownloadAsset}
          isFullscreenEnabled={isFullscreenEnabled}
          onClick={this.handlePresentationClick}
          onGoToSlideClick={this.handleGoToSlideClick}
          onNextClick={this.handleNextClick}
          onPrevClick={this.handlePrevClick}
          onDownloadClick={this.handleDownloadClick}
          onAfterChange={this.handleAfterSlideChange}
          onFullscreenClick={() => {
            this.handleFullscreenClickAndFocus(!isFullscreenEnabled);
          }}
        />
      </FullscreenManager>
    );
  }

}

Presentation.propTypes = {
  height: PropTypes.string,
  slides: PropTypes.array.isRequired,
  downloadLink: PropTypes.string,
  displayFilename: PropTypes.string.isRequired,
  focusOnMount: PropTypes.bool,
  canDownloadAsset: PropTypes.bool.isRequired,
  onDownloadClick: PropTypes.func,
  onPresentationLoad: PropTypes.func,
  onSlideDisplay: PropTypes.func
};

export default Presentation;
